/**
 * Signature re-capture — phone confirmation screen (PF-6119)
 *
 * Screen 1 of `/signature-recapture/:recaptureId`. Mirrors `WebOnboardingPhoneVerificationScreen` for
 * structure, `PhoneNumberInput` usage, phone normalisation and the two-strike error copy. Differs by
 * calling `verifySignatureRecapturePhone` instead of signing in, and by carrying no `signUpId` — a
 * missing or unknown `recaptureId` fails on submit with the same message as a wrong number, so the
 * page never confirms which recapture ids exist.
 */
import type { VerifySignatureRecapturePhoneResponse } from '@physiofit-digital/physiofit_types';
import { FirebaseError } from 'firebase/app';
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { space } from '../../../styles/onboardingUiTokens';
import { verifySignatureRecapturePhone } from '../api/verifySignatureRecapturePhone';
import { signatureRecaptureCopy } from '../config/copy';
import { Body, H1, OnboardingBody, OnboardingFooter, OnboardingHeader, OnboardingLayout, PhoneNumberInput } from '../ui';
import { toCallablePhone } from '../utils/normalizePhone';

const copy = signatureRecaptureCopy.phone;

export type PhoneScreenProps = {
  recaptureId: string;
  onVerified: (result: VerifySignatureRecapturePhoneResponse) => void;
};

export function PhoneScreen({ recaptureId, onVerified }: PhoneScreenProps) {
  const [countryCode, setCountryCodeState] = useState('+49');
  const [nationalNumber, setNationalNumberState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const permissionDeniedStreakRef = useRef(0);
  const [permissionDeniedStreak, setPermissionDeniedStreak] = useState(0);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const resetPhoneErrors = useCallback(() => {
    setFieldError(undefined);
    permissionDeniedStreakRef.current = 0;
    setPermissionDeniedStreak(0);
  }, []);

  const setCountryCode = useCallback(
    (value: string) => {
      setCountryCodeState(value);
      resetPhoneErrors();
    },
    [resetPhoneErrors],
  );

  const setNationalNumber = useCallback(
    (value: string) => {
      setNationalNumberState(value);
      resetPhoneErrors();
    },
    [resetPhoneErrors],
  );

  const handleConfirm = useCallback(async () => {
    const fullPhoneNumber = toCallablePhone(countryCode, nationalNumber);
    if (!fullPhoneNumber || fullPhoneNumber.length < 8) {
      setFieldError(copy.mismatch);
      return;
    }

    setSubmitting(true);
    setFieldError(undefined);

    try {
      const result = await verifySignatureRecapturePhone({ recaptureId, phone: fullPhoneNumber });
      permissionDeniedStreakRef.current = 0;
      setPermissionDeniedStreak(0);
      onVerified(result);
    } catch (error) {
      const isPermissionDenied = error instanceof FirebaseError && error.code === 'functions/permission-denied';

      if (isPermissionDenied) {
        permissionDeniedStreakRef.current += 1;
        const streak = permissionDeniedStreakRef.current;
        setPermissionDeniedStreak(streak);
        setFieldError(streak >= 2 ? copy.mismatchRepeated : copy.mismatch);
        return;
      }

      setFieldError(copy.mismatch);
    } finally {
      setSubmitting(false);
    }
  }, [countryCode, nationalNumber, recaptureId, onVerified]);

  const forwardDisabled =
    submitting || nationalNumber.replace(/\D/g, '').length < 6 || countryCode.replace(/\D/g, '').length < 1;

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={3}
      onForward={handleConfirm}
      forwardLabel={copy.forwardLabel}
      forwardDisabled={forwardDisabled}
      forwardBusy={submitting}
    >
      <OnboardingHeader hideBackButton />
      <OnboardingBody disableSupportHint>
        <ContentStack>
          <H1>{copy.title}</H1>
          <Body variant="document">{copy.body}</Body>
          <PhoneNumberInput
            id="recapture-phone"
            countryLabel={copy.countryLabel}
            nationalLabel={copy.nationalLabel}
            countryCode={countryCode}
            nationalNumber={nationalNumber}
            setCountryCode={setCountryCode}
            setNationalNumber={setNationalNumber}
            phoneError={fieldError}
            disabled={submitting}
          />
          {permissionDeniedStreak >= 2 ? <Body variant="document">{copy.supportHint}</Body> : null}
        </ContentStack>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;
